import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { get, intersection } from 'lodash';

import { useDecision } from '@optimizely/react-sdk';
import { OPTIMIZELY_FLAGS } from 'config/optimizely';
import { trackDecision } from 'lib/optimizelyDebug';
import UnitPageV2 from 'pages/UnitPageV2/UnitPage';

import { createBooking } from 'store/bookings/slice';

import { H1, H2 } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Page, Group } from 'components/core/Page';
import { Card } from 'components/core/Card';
import { UnitReservationForm } from 'components/unit/UnitReservationForm';
import { SelectedUnitCard } from 'components/unit/SelectedUnitCard';
import { SelectedFacilityCard } from 'components/unit/SelectedFacilityCard';
import { getUnitFacilityLookup } from 'store/selectors/unitLookup';
import { withPageViewTracking } from 'components/core/library/tracking-utils';

import visaImage from 'assets/images/credit-cards/visa.png';
import mastercardImage from 'assets/images/credit-cards/mastercard.png';
import amexImage from 'assets/images/credit-cards/amex.png';
import discoverImage from 'assets/images/credit-cards/discover.png';

import './unitPage.scss';

const icons = {
	Visa: visaImage,
	Mastercard: mastercardImage,
	AMEX: amexImage,
	Discover: discoverImage,
};

function withUnitPageState(Component) {
	function UnitPageWithState(ownProps) {
		const dispatch = useDispatch();
		const selectorResult = useSelector((state) => getUnitFacilityLookup(state, ownProps));
		const actions = useMemo(
			() => bindActionCreators({ createBooking }, dispatch),
			[dispatch],
		);
		return <Component {...ownProps} {...selectorResult} actions={actions} />;
	}
	UnitPageWithState.displayName = 'UnitPageWithState';
	return UnitPageWithState;
}

export const enhance = (Component) =>
	withUnitPageState(withPageViewTracking(PAGE_TYPES.RESERVE)(Component));

export const UnitPageV1 = ({ actions, unit, facility, phone }) => {
	const creditCards = useMemo(
		() =>
			intersection(
				get(facility, 'amenities.acceptedCreditCards', []),
				Object.keys(icons),
			),
		[facility],
	);

	return (
		<Page className="unit-page">
			<div className="page-container">
				<Group>
					<H1 size="large">Complete your reservation</H1>
				</Group>
				<div className="unit-page-content">
					<div className="unit-page-details">
						<SelectedUnitCard unit={unit} />
						{!!creditCards.length && (
							<Card className="credit-card-card">
								<H2
									className="selected-unit-card-header"
									strong
									size="small"
								>
									Credit cards accepted
								</H2>
								{creditCards.map((card) => (
									<Image
										className="credit-card"
										key={card}
										src={icons[card]}
									/>
								))}
							</Card>
						)}
						<SelectedFacilityCard
							facility={facility}
							formattedPhoneNumber={phone.localSparephone}
							unformattedPhoneNumber={
								phone.localSparephoneUnformatted
							}
						/>
					</div>
					<div className="unit-page-reservation-form">
						<Card>
							<div className="divider">
								<H2 size="medium">
									Lock in this rate now. No credit card. No
									obligation.
								</H2>
							</div>
							<UnitReservationForm
								facility={facility}
								unit={unit}
								onSubmit={actions.createBooking}
								phone={phone}
							/>
						</Card>
					</div>
				</div>
			</div>
		</Page>
	);
};

UnitPageV1.propTypes = {
	actions: PropTypes.shape({
		createBooking: PropTypes.func,
	}),
	unit: PropTypes.object,
	facility: PropTypes.object,
	phone: PropTypes.object,
};

export const UnitPage = (props) => {
	// Use SDK decision hook directly instead of Redux state
	const [decision] = useDecision(OPTIMIZELY_FLAGS.FACILITY_PAGE_REDESIGN.key);
	trackDecision(OPTIMIZELY_FLAGS.FACILITY_PAGE_REDESIGN.key, decision);
	const isFacilityRedesign = decision?.enabled || false;

	return isFacilityRedesign ? (
		<UnitPageV2 {...props} />
	) : (
		<UnitPageV1 {...props} />
	);
};

export default enhance(UnitPage);
